/**
 * PoolingManager.tsx
 * Talkroute
 *
 * Created by Vel Genov on 14/5/20
 * Copyright (c) 2020 Talkroute. All right reserved.
 */

import React, { useEffect } from "react";
import { ReactTimeoutProps } from "react-timeout";
import { useDispatch } from "react-redux";

import { fetchPhoneNumbers } from "../actions/preferencesActions";
import {
  fetchVoicemailBoxes,
  fetchVoicemails,
} from "../actions/voicemailActions";
import {
  fetchNewConversations,
  getAllMessagesSinceLast,
} from "../actions/conversationActions";
import { fetchCallHistoryUpdates } from "../actions/callHistoryActions";
import { fetchBlockedNumbers } from "../actions/callActions";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import _ from "lodash";
import useContactsManager from "@helpers/hooks/useContactsManager";
import { refreshPhonenumbers } from "@reducers/contactsReducer";
import { getUserPreferences } from "@actions/userActions";

const ContactsManager = () => {
  useContactsManager();
  return <></>
}
function useInterval(callback: () => void, delay: number | null) {
  const intervalRef = React.useRef<any>();
  const callbackRef = React.useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay) {
      intervalRef.current = setInterval(() => callbackRef.current(), delay);

      return () => clearInterval(intervalRef.current);
    }
  }, [delay]);

  return intervalRef;
}
interface IProps extends ReactTimeoutProps {
  children?: any;
}

const ONE_MINUTE_INTERVAL = 60 * 1000;
const GET_PHONE_NUMBERS_INTERVAL = ONE_MINUTE_INTERVAL;
const GET_VOICE_EMAILS_INTERVAL = ONE_MINUTE_INTERVAL;
const GET_CONVERSATIONS_INTERVAL = ONE_MINUTE_INTERVAL;
const GET_CALL_HISTORY_INTERVAL = ONE_MINUTE_INTERVAL;
const GET_BLOCKED_NUMBERS_INTERVAL = ONE_MINUTE_INTERVAL;
const GET_CURRENT_TOPIC_MESSAGES_INTERVAL = ONE_MINUTE_INTERVAL;
const GET_CONTACTS_DATA_INTERVAL = 5 * ONE_MINUTE_INTERVAL;
const GET_CALL_HISTORY_LAST_VISITED_INTERVAL = ONE_MINUTE_INTERVAL;
const PoolingManager = ({ children }: IProps) => {
  const isAuthenticated = useAppSelector(
    (state) => state.authentication.isUserAuthenticated
  );
  const userTokenReady = useAppSelector(
    (state) => state.authentication.userTokenReady
  );
  const currentConversationId = useAppSelector(
    (state) => state.conversations.selectedConversationId
  );
  const conversations = useAppSelector((state) => state.conversations.data);
  const dispatch = useDispatch();


  useInterval(
    () => {
      dispatch(fetchVoicemails(true));
      dispatch(fetchVoicemailBoxes());
    },
    userTokenReady && isAuthenticated ? GET_VOICE_EMAILS_INTERVAL : null
  );

  useInterval(
    () => dispatch(fetchPhoneNumbers()),
    userTokenReady && isAuthenticated ? GET_PHONE_NUMBERS_INTERVAL : null
  );

  useInterval(
    () => dispatch(fetchNewConversations()),
    userTokenReady && isAuthenticated ? GET_CONVERSATIONS_INTERVAL : null
  );

  useInterval(
    () => dispatch(fetchCallHistoryUpdates(true)),
    userTokenReady && isAuthenticated ? GET_CALL_HISTORY_INTERVAL : null
  );

  useInterval(
    () => dispatch(refreshPhonenumbers()),
    userTokenReady && isAuthenticated ? GET_CONTACTS_DATA_INTERVAL : null
  );

  useInterval(
    () => dispatch(fetchBlockedNumbers()),
    userTokenReady && isAuthenticated ? GET_BLOCKED_NUMBERS_INTERVAL : null
  );

  useInterval(
    () => dispatch(getUserPreferences()),
    userTokenReady && isAuthenticated ? GET_CALL_HISTORY_LAST_VISITED_INTERVAL : null
  );

  useInterval(
    () => {
      dispatch(
        getAllMessagesSinceLast({
          conversationId: currentConversationId,
          conversations: conversations,
        })
      );
    },
    userTokenReady && isAuthenticated && currentConversationId
      ? GET_CURRENT_TOPIC_MESSAGES_INTERVAL
      : null
  );
  return <>
    <ContactsManager />
    {children}
   </>;
};

export default PoolingManager;
